import { useEffect, useState } from 'react';

import { Alert, Box, CloseButton, Image } from '@zillow/constellation-10';

import { useRentalManagerGoogleMapsWebApiHandler_addSignatureAndKeyToUrl } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useScreenSize } from '../../hooks/useScreenSize';

import CorrectMapPinCSEvents from '../../../srcLegacy/shared/model/ClickstreamEvent/CorrectMapPinCSEvents';

import { useAuth } from '../AuthProvider';

import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { UserProps } from '../../../srcLegacy/shared/model/User';

const MAP_MARKER = 'https://nodes3cdn.hotpads.com/google-maps-icons/MapMarker_home.png';

export type Props = {
  address: string;
  marginTop?: string | Record<string, string>;
  additionalMarkers?: string;
  size?: string;
  scale?: string | number;
  showImpreciseLocationAlert?: boolean;
  isMultiplexType?: boolean;
  user?: Partial<RentalManagerAuthCheckUserInfoWebApiDto> | Partial<UserProps>;
};

const DEFAULT_MAP_SIZE = '388x291';

const StaticMap = ({
  address,
  marginTop,
  additionalMarkers,
  size = DEFAULT_MAP_SIZE,
  scale,
  showImpreciseLocationAlert = false,
  isMultiplexType = false,
  user: userProp,
}: Props) => {
  const [mapUrl, setMapUrl] = useState('');
  const authContext = useAuth?.();
  const user = userProp ?? authContext?.user;
  const { isScreenLgGTE } = useScreenSize();

  const { mutate: signUrl } = useRentalManagerGoogleMapsWebApiHandler_addSignatureAndKeyToUrl();

  const confirmAddressButtonTitle = isMultiplexType ? 'Yes, continue' : 'Confirm';

  useEffect(() => {
    const encodedAddress = encodeURIComponent(address);
    const mapSize = showImpreciseLocationAlert ? (isScreenLgGTE ? '388x388' : '388x554') : size;
    const gmapsAPIParams = new URLSearchParams({
      center: encodedAddress,
      size: mapSize,
      style: 'feature:poi|visibility:off',
      markers: `${additionalMarkers ? `${additionalMarkers}` : ''}&markers=icon:${MAP_MARKER}|${encodedAddress}`,
      ...(scale && { scale: String(scale) }),
    });

    const url = `https://maps.googleapis.com/maps/api/staticmap?${gmapsAPIParams.toString()}`;

    signUrl(
      { postData: { url } },
      {
        onSuccess: (response: { signedUrl: string }) => {
          setMapUrl(response.signedUrl);
        },
      },
    );
  }, [isScreenLgGTE, showImpreciseLocationAlert, signUrl]);

  useEffect(() => {
    if (showImpreciseLocationAlert && user) {
      CorrectMapPinCSEvents.ViewImpreciseLocationAlert({ user });
    }
  }, [showImpreciseLocationAlert, user]);

  return (
    Boolean(mapUrl) && (
      <>
        {showImpreciseLocationAlert ? (
          <Box
            css={{
              position: 'relative',
              display: 'inline-block',
              flex: size !== DEFAULT_MAP_SIZE ? 'none' : { lg: '1 1 0' },
              width: { base: 'fit-content', lg: 'auto' },
            }}
          >
            <Alert
              tone="warning"
              role="alert"
              closeButton={<CloseButton />}
              css={{
                position: 'absolute',
                top: '100',
                zIndex: 2,
                margin: '0px {spacing.200}',
              }}
              data-testid="StaticMap-ImpreciseLocationAlert"
              data-eventid={CorrectMapPinCSEvents.ViewImpreciseLocationAlert.data_event_id}
              data-eventdescription="View alert event"
            >
              If the address is correct but the location is wrong, select &quot;{confirmAddressButtonTitle}&quot; to
              proceed. We&apos;ll fix it in an upcoming step.
            </Alert>
            <Image src={mapUrl} alt={address} css={{ marginTop, width: { lg: '100%' } }} />
          </Box>
        ) : (
          <Image src={mapUrl} alt={address} css={{ marginTop }} />
        )}
      </>
    )
  );
};

export default StaticMap;
